import '../styles/index.css'
import { useMemo, useEffect, useState } from 'react'
import { generateThemeStylesheet } from '../utils'
import { LatestWebchatTheme } from '@bpinternal/shared'

type Props = Partial<LatestWebchatTheme>

/**
 * Loads a Google Font by creating a link element and injecting it into the document head
 * @param fontFamily - The font family name to load
 * @param fontVariants - Array of font weight variants to load
 * @returns Promise that resolves when the font is loaded or rejects on error
 */
const loadGoogleFont = (fontFamily: string, fontVariants: string[]) => {
  const fontName = fontFamily.replace(/\s+/g, '+')
  const sanitizedFontFamily = fontFamily.replace(/[^a-zA-Z0-9\s\-_]/g, '').trim()
  if (!sanitizedFontFamily) return

  const weights = fontVariants.filter((v) => /^\d+$/.test(v)).join(';')
  const fontUrl = `https://fonts.googleapis.com/css2?family=${fontName}:wght@${weights}&display=swap`

  const existingLink = document.querySelector(`link[href="${fontUrl}"]`)
  if (existingLink) return

  const link = document.createElement('link')
  link.rel = 'stylesheet'
  link.href = fontUrl

  document.head.appendChild(link)
}

/**
 * StylesheetProvider component that manages theme styles and font loading
 */
export const StylesheetProvider = ({
  color,
  fontFamily,
  radius,
  themeMode,
  variant,
  headerVariant,
  additionalStylesheetUrl,
}: Props) => {
  useMemo(() => {
    if (fontFamily) {
      loadGoogleFont(fontFamily, ['100', '200', '300', '400', '500', '600', '700', '800'])
    }
  }, [fontFamily])

  const cssString = useMemo(
    () => generateThemeStylesheet({ color, fontFamily, radius, themeMode, variant, headerVariant }),
    [color, fontFamily, radius, themeMode, variant, headerVariant]
  )

  return (
    <>
      {additionalStylesheetUrl && <link rel="stylesheet" href={additionalStylesheetUrl} />}
      <style>{cssString}</style>
    </>
  )
}
